// Global Instructions Rule Applied!
import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { Space } from 'antd'
import Button from '../Button.jsx'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { InputManager } from '../InputManager/index.ts'
import { FieldConfig, FieldTypeEnum } from '../InputManager/types.ts'

interface TaskFilterDropdownProps {
  selectedKeys: any[]
  setSelectedKeys: (keys: any[]) => void
  confirm: () => void
  clearFilters: () => void
  onFilterChange?: (filters: Array<{ key: string; value: any }>) => void
  moduleKey: string
}

interface FilterValues {
  hasOpen: boolean
  assignee: string[]
  dueDate: string | null
}

/**
 * Generic Task Filter Dropdown Component
 * Provides filtering options for task columns across different modules
 * Uses moduleKey to determine the correct Redux state paths
 */
const TaskFilterDropdown: React.FC<TaskFilterDropdownProps> = ({
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  onFilterChange,
  moduleKey
}) => {
  // Map moduleKey to Redux state paths
  // Note: Companies table uses moduleKey 'accounts' but Redux state is 'companies'
  const getReduxStatePaths = useCallback((key: string) => {
    switch (key) {
      case 'contacts':
        return {
          activeFilters: (state: any) => state.contacts?.activeFilters || [],
          filterOptions: (state: any) => state.contacts?.filterOptions?.data?.task_assignees__contact_id || []
        }
      case 'deals':
        return {
          activeFilters: (state: any) => state.deals.deal.activeFilters || [],
          filterOptions: (state: any) => state.deals.deal.filterOptions.data?.task_assignees__contact_id || []
        }
      case 'companies':
        return {
          activeFilters: (state: any) => state.companies?.activeFilters || [],
          filterOptions: (state: any) => state.companies?.filterOptions?.data?.task_assignees__contact_id || []
        }
      case 'accounts':
        // Companies table uses 'accounts' as moduleKey but 'companies' in Redux state
        // Check if companies state exists, otherwise fall back to empty
        return {
          activeFilters: (state: any) => state.companies?.activeFilters || [],
          filterOptions: (state: any) => state.companies?.filterOptions?.data?.task_assignees__contact_id || []
        }
      case 'prospects':
        // Fundraising uses 'prospects' as moduleKey but 'fundraising' in Redux state
        return {
          activeFilters: (state: any) => state.fundraising?.activeFilters || [],
          filterOptions: (state: any) => state.fundraising?.filterOptions?.data?.task_assignees__contact_id || []
        }
      default:
        return {
          activeFilters: (state: any) => [],
          filterOptions: (state: any) => []
        }
    }
  }, [])

  const statePaths = getReduxStatePaths(moduleKey)
  const activeFilters = useSelector(statePaths.activeFilters)
  const taskAssigneesOptions = useSelector(statePaths.filterOptions)

  const currentFilter = useMemo(() => {
    if (activeFilters && activeFilters.length > 0) {
      const tasksFilter = activeFilters.find((filter: any) => filter.key === 'prog__tasks')
      return tasksFilter && tasksFilter.value ? tasksFilter.value : {}
    }
    return {}
  }, [activeFilters])

  const [fieldValues, setFieldValues] = useState<FilterValues>({
    hasOpen: currentFilter.hasOpen || false,
    assignee: currentFilter.assignee || [],
    dueDate: currentFilter.dueDate || null
  })

  useEffect(() => {
    setFieldValues({
      hasOpen: currentFilter.hasOpen || false,
      assignee: currentFilter.assignee || [],
      dueDate: currentFilter.dueDate || null
    })
  }, [currentFilter])

  const handleFieldChange = useCallback((key: string, value: any) => {
    setFieldValues((prev) => {
      if (key === 'dueDate') {
        const dateValue = value ? (dayjs.isDayjs(value) ? value.format('YYYY-MM-DD') : value) : null
        return { ...prev, [key]: dateValue }
      }
      if (key === 'assignee') {
        if (Array.isArray(value)) {
          const ids = value.map((item: any) => (typeof item === 'object' && item?.value ? item.value : item))
          return { ...prev, [key]: ids }
        }
        return { ...prev, [key]: [] }
      }
      return { ...prev, [key]: value }
    })
  }, [])

  const handleApplyFilters = () => {
    const newFilters = {
      hasOpen: fieldValues.hasOpen,
      assignee: fieldValues.assignee || [],
      assigneeNames: (fieldValues.assignee || []).map((contactId) => {
        const option = taskAssigneesOptions?.find((opt: any) => opt.value === contactId)
        return option ? option.label : contactId
      }),
      dueDate: fieldValues.dueDate || null
    }

    const hasActiveFilters =
      newFilters.hasOpen || (newFilters.assignee && newFilters.assignee.length > 0) || newFilters.dueDate

    if (hasActiveFilters) {
      const newFilterArray = [
        {
          key: 'prog__tasks',
          value: newFilters
        }
      ]

      setSelectedKeys([newFilters])
      onFilterChange?.(newFilterArray)
    } else {
      setSelectedKeys([])
      onFilterChange?.([])
    }

    confirm()
  }

  const handleClearFilters = () => {
    setFieldValues({
      hasOpen: false,
      assignee: [],
      dueDate: null
    })

    setSelectedKeys([])
    onFilterChange?.([])
    confirm()
  }

  const fields: FieldConfig[] = useMemo(() => {
    return [
      {
        key: 'hasOpen',
        type: FieldTypeEnum.CHECKBOX,
        value: fieldValues.hasOpen || false,
        checkboxLabel: 'Has open tasks',
        editable: true
      },
      {
        key: 'assignee',
        label: 'Assigned To',
        type: FieldTypeEnum.CONTACT,
        value: fieldValues.assignee || [],
        options: taskAssigneesOptions || [],
        mode: 'multiple',
        placeholder: 'Select contacts...',
        editable: true,
        allowAddNew: false
      },
      {
        key: 'dueDate',
        label: 'Due on or before',
        type: FieldTypeEnum.DATE,
        value: fieldValues.dueDate ? dayjs(fieldValues.dueDate) : null,
        format: 'YYYY-MM-DD',
        allowClear: true,
        placeholder: 'Select date...',
        editable: true
      }
    ]
  }, [fieldValues, taskAssigneesOptions])

  return (
    <div className='w-80 p-2'>
      <div className='space-y-1'>
        {fields.map((field) => (
          <div key={field.key} className='mb-2'>
            <InputManager
              inputMode='edit'
              fields={[field]}
              labelPosition='above'
              onChange={handleFieldChange}
              fieldWrapping={true}
            />
          </div>
        ))}
      </div>

      <div className='flex justify-between space-x-2 pt-2 mt-2 border-gray-200'>
        <Button variant='cancel' size='small' onClick={handleClearFilters}>
          Clear
        </Button>
        <Space size='small'>
          <Button variant='primary' size='small' onClick={handleApplyFilters}>
            Apply
          </Button>
        </Space>
      </div>
    </div>
  )
}

export default TaskFilterDropdown
